import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

/**
 * GİDERLER — resmî faturası olmayan küçük harcamalar (otopark, kasa fişi, harç).
 *
 * ⚠ BU İSTEMCİDE KDV İNDİRİM ALANI YOK ve olmayacak. Sunucuda da kolon yok:
 * AR n° 3 md. 3 §1, 1° indirim hakkını kullanabilmek için usulüne uygun bir
 * FATURA elde tutulmasını şart koşuyor ve fiş bunu sağlamaz. Alanı gösterip
 * "girmeyin" demek yerine HİÇ GÖSTERMEMEK, sorunun sorulmasını engelliyor.
 *
 * ⚠ ALIŞ FATURASIYLA KARIŞTIRILMAMALI: fatura VARSA kayıt "Alış faturaları"na
 * girer ve orada KDV indirimi vardır. Ayrım belgenin türüdür, tutarın büyüklüğü
 * değil.
 */

export type ExpenseStatus = 'DRAFT' | 'CONFIRMED';
export type PaidFrom = 'BANK' | 'CASH' | 'PRIVATE';

export const STATUS_LABELS: Record<ExpenseStatus, string> = {
  DRAFT: 'Taslak',
  CONFIRMED: 'Onaylandı',
};

/**
 * ⚠ "ÖZEL CEPTEN" VARSAYILAN, çünkü eenmanszaak'ta en sık olan budur ve
 * seçim mali bir gerçeği taşır: özel cepten ödenen gider işletmenin
 * ortak/cari hesabına borç yazılır.
 */
export const PAID_FROM_LABELS: Record<PaidFrom, string> = {
  PRIVATE: 'Özel cepten',
  BANK: 'İşletme banka hesabından',
  CASH: 'İşletme kasasından',
};

export interface Expense {
  id: string;
  status: ExpenseStatus;
  expenseDate: string;
  description: string;
  /** ⚠ KDV DÂHİL. Fişte matrah/KDV ayrımı yoktur; ayrılmış hâli SAKLANMIYOR. */
  amountInclVat: string;
  incomeTaxDeductiblePct: string;
  paidFrom: PaidFrom;
  expenseAccountCode: string | null;
  attachmentStoragePath: string | null;
  attachmentContentType: string | null;
  attachmentByteLength: number | null;
  notes: string | null;
  confirmedAt: string | null;
}

export interface ListExpensesParams {
  page?: number;
  pageSize?: number;
  status?: ExpenseStatus;
  missingAttachment?: boolean;
  q?: string;
}

export function useExpenses(params: ListExpensesParams) {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  if (params.status) qs.set('status', params.status);
  if (params.missingAttachment !== undefined) {
    qs.set('missingAttachment', String(params.missingAttachment));
  }
  if (params.q) qs.set('q', params.q);

  return useQuery({
    queryKey: ['expenses', params],
    queryFn: () => api.get<{ items: Expense[]; total: number }>(`/expenses?${qs.toString()}`),
  });
}

export function useCreateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => api.post<Expense>('/expenses', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['expenses'] }),
  });
}

export function useConfirmExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Expense>(`/expenses/${id}/confirm`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['expenses'] }),
  });
}

export function useReopenExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<Expense>(`/expenses/${id}/reopen`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['expenses'] }),
  });
}

export function useDeleteExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/expenses/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['expenses'] }),
  });
}

/**
 * Fişin görüntüsünü yükler.
 *
 * ⚠ GÖVDE HAM BAYTLARDIR, `FormData` DEĞİL — sunucu multipart beklemiyor.
 * `Content-Type` dosyanın kendi tipidir ve sunucu ona GÜVENMEZ: ilk baytlarla
 * karşılaştırır.
 */
export function useUploadExpenseReceipt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      api.postRaw<Expense>(`/expenses/${id}/attachment`, file, file.type),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['expenses'] }),
  });
}

/** Arşivdeki fiş görüntüsünü indirir — sunucu özeti doğrulamadan vermez. */
export function downloadExpenseReceipt(id: string) {
  return api.file(`/expenses/${id}/attachment`);
}
